<template>
  <div v-if="loading" class="p-6 text-center">
    <div
      class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
    ></div>
    <p
      class="mt-4 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-xs"
    >
      Loading car details...
    </p>
  </div>
  <div class="p-6 space-y-6 text-slate-900 dark:text-white" v-else-if="car.id">
    <!-- Header -->
    <div
      class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
    >
      <div class="flex items-center gap-4">
        <BaseButton
          type="secondary"
          @click="$router.push('/cars')"
          class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
        >
          {{ $t("back") }}
        </BaseButton>
        <div>
          <h1 class="text-2xl font-black">{{ car.model_name }}</h1>
          <p
            class="text-[10px] font-bold text-primary uppercase tracking-widest"
          >
            {{ car.brand?.name_en }} • {{ car.year }}
          </p>
        </div>
      </div>
      <div class="flex gap-2 w-full md:w-auto">
        <BaseButton
          v-if="canManageCar()"
          type="secondary"
          @click="$router.push(`/cars/${car.id}/unavailable-periods`)"
          class="flex-1 md:flex-none !rounded-xl bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20"
        >
          {{ $t("unavailable_periods") }}
        </BaseButton>
        <BaseButton
          v-if="canManageCar()"
          type="primary"
          @click="$router.push(`/cars/edit/${car.id}`)"
          class="flex-1 md:flex-none !rounded-xl bg-primary text-white shadow-lg shadow-primary/20"
        >
          {{ $t("edit") }}
        </BaseButton>
        <BaseButton
          v-if="canManageCar()"
          type="danger"
          @click="deleteCar"
          class="flex-1 md:flex-none !rounded-xl bg-red-500 text-white shadow-lg shadow-red-500/20"
        >
          {{ $t("delete") }}
        </BaseButton>
      </div>
    </div>

    <!-- Main Grid -->
    <div class="grid lg:grid-cols-3 gap-6">
      <!-- Left & Center Column (Main Info) -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Status & Key Specs -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 transition-colors"
        >
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-black uppercase tracking-tight">
              {{ $t("overview") }}
            </h3>
            <span
              :class="{
                'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-500':
                  car.status === 'available',
                'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-500':
                  car.status === 'rented',
                'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-500':
                  car.status === 'maintenance',
              }"
              class="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-transparent dark:border-current"
            >
              {{ car.status ? $t(car.status) : "—" }}
            </span>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div class="space-y-1">
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
              >
                {{ $t("plate_number") }}
              </p>
              <p class="font-bold text-slate-700 dark:text-slate-200">
                {{ car.plate_number }}
              </p>
            </div>
            <div class="space-y-1">
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
              >
                {{ $t("color") }}
              </p>
              <p class="font-bold text-slate-700 dark:text-slate-200">
                {{ car.color }}
              </p>
            </div>
            <div class="space-y-1">
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
              >
                {{ $t("seats") }}/{{ $t("doors") }}
              </p>
              <p class="font-bold text-slate-700 dark:text-slate-200">
                {{ car.number_of_seats }}S / {{ car.doors }}D
              </p>
            </div>
            <div class="space-y-1">
              <p
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
              >
                {{ $t("fuel_efficiency") }}
              </p>
              <p class="font-bold text-slate-700 dark:text-slate-200">
                {{ car.fuel_efficiency }}
              </p>
            </div>
          </div>
        </div>

        <!-- Details Grid (Type, Location, Availability) -->
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Car Type & Mechanical -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
            >
              {{ $t("technical_specs") }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("category")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.category?.name_en
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("vehicle_type")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.vehicle_type?.name_en
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("fuel_type")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.fuel_type?.name_en
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("transmission")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.transmission_type?.name_en
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("fuel_policy")
                }}</span>
                <span class="text-sm font-bold uppercase">{{
                  car.fuel_policy ? $t(car.fuel_policy) : "—"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Location Info -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
            v-if="car.location"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
            >
              {{ $t("location") }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase"
                  >{{ $t("city") }}/{{ $t("state") }}</span
                >
                <span class="text-sm font-bold"
                  >{{ car.location.city?.name_en }},
                  {{ car.location.state }}</span
                >
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("street_address")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.location.street_address
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("postal_code")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.location.postal_code
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("country")
                }}</span>
                <span class="text-sm font-bold">{{
                  car.location.country?.name_en
                }}</span>
              </div>
            </div>
          </div>

          <!-- Availability -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
            v-if="car.availability"
          >
            <div
              class="flex justify-between items-center border-b dark:border-white/5 pb-2"
            >
              <h3
                class="text-sm font-black uppercase tracking-widest text-primary"
              >
                {{ $t("availability") }}
              </h3>
              <button
                v-if="canManageCar()"
                @click="$router.push(`/cars/${car.id}/unavailable-periods`)"
                class="text-[10px] font-bold text-amber-500 hover:text-amber-600 transition-colors uppercase tracking-wider flex items-center gap-1.5"
              >
                <i class="fas fa-calendar-times"></i>
                {{ $t("unavailable_periods") }}
              </button>
            </div>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("notice_period")
                }}</span>
                <span class="text-sm font-bold"
                  >{{ car.availability.advance_notice_hours }} Hours</span
                >
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("trip_duration")
                }}</span>
                <span class="text-sm font-bold"
                  >{{ car.availability.minimum_trip_days }} -
                  {{ car.availability.max_trip_days }} Days</span
                >
              </div>
              <div class="flex flex-col gap-2 pt-1">
                <span class="text-xs font-bold text-slate-400 uppercase">{{
                  $t("available_days")
                }}</span>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="day in car.availability.available_days"
                    :key="day"
                    class="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded"
                  >
                    {{ day }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Protection Plan -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
            v-if="car.protection_plan"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
            >
              {{ $t("protection_plan") }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase"
                  >Plan Name</span
                >
                <span class="text-sm font-bold">{{
                  car.protection_plan.name_en
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs font-bold text-slate-400 uppercase"
                  >Fee / Day</span
                >
                <span class="text-sm font-bold text-green-500"
                  >{{ car.protection_plan.price_per_day }}
                  <span class="text-[10px] uppercase">{{
                    car.currency
                  }}</span></span
                >
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-bold text-slate-400 uppercase"
                  >Details</span
                >
                <p class="text-xs text-slate-500 dark:text-slate-400 italic">
                  {{ car.protection_plan.description_en }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Lists (Features, Pickup, Rules, Experiences) -->
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Features -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("features") }}
            </h3>
            <div v-if="car.features?.length" class="flex flex-wrap gap-2">
              <span
                v-for="f in car.features"
                :key="f.id"
                class="bg-slate-100 dark:bg-dark-input text-slate-600 dark:text-slate-300 text-[10px] font-bold px-2 py-1 rounded-lg border dark:border-white/5"
              >
                {{ f.name_en }}
              </span>
            </div>
            <p v-else class="text-xs text-slate-400 italic">
              {{ $t("no_features") }}
            </p>
          </div>

          <!-- Pickup Options -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("pickup_options") }}
            </h3>
            <div v-if="car.pickup_options?.length" class="flex flex-wrap gap-2">
              <span
                v-for="p in car.pickup_options"
                :key="p.id"
                class="bg-green-50 dark:bg-green-500/5 text-green-600 dark:text-green-500 text-[10px] font-bold px-2 py-1 rounded-lg border border-green-100 dark:border-green-500/10"
              >
                {{ p.name_en }}
              </span>
            </div>
            <p v-else class="text-xs text-slate-400 italic">
              {{ $t("no_pickup") }}
            </p>
          </div>

          <!-- Rules -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("car_rules") }}
            </h3>
            <div v-if="car.rules?.length" class="flex flex-wrap gap-2">
              <span
                v-for="r in car.rules"
                :key="r.id"
                class="bg-red-50 dark:bg-red-500/5 text-red-600 dark:text-red-500 text-[10px] font-bold px-2 py-1 rounded-lg border border-red-100 dark:border-red-500/10"
              >
                {{ r.name_en }}
              </span>
            </div>
            <p v-else class="text-xs text-slate-400 italic">
              {{ $t("no_rules") }}
            </p>
          </div>

          <!-- Experiences -->
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5"
          >
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("elevate_experiences") }}
            </h3>
            <div
              v-if="car.elevate_experiences?.length"
              class="flex flex-wrap gap-2"
            >
              <span
                v-for="e in car.elevate_experiences"
                :key="e.id"
                class="bg-purple-50 dark:bg-purple-500/5 text-purple-600 dark:text-purple-500 text-[10px] font-bold px-2 py-1 rounded-lg border border-purple-100 dark:border-purple-500/10"
              >
                {{ e.name_en }}
              </span>
            </div>
            <p v-else class="text-xs text-slate-400 italic">
              {{ $t("no_experiences") }}
            </p>
          </div>
        </div>

        <!-- Description & Images Section -->
        <div
          class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-6"
        >
          <div>
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("description") }}
            </h3>
            <div class="grid md:grid-cols-2 gap-6">
              <div
                class="p-4 bg-slate-50 dark:bg-dark-input rounded-xl border dark:border-white/5"
              >
                <p
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1"
                >
                  English
                </p>
                <p class="text-sm leading-relaxed">
                  {{ car.description_en || "N/A" }}
                </p>
              </div>
              <div
                class="p-4 bg-slate-50 dark:bg-dark-input rounded-xl border dark:border-white/5 text-right"
              >
                <p
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1"
                >
                  Arabic
                </p>
                <p class="text-sm leading-relaxed font-arabic">
                  {{ car.description_ar || "N/A" }}
                </p>
              </div>
            </div>
            <div
              class="mt-4 p-4 bg-amber-50 dark:bg-amber-500/5 rounded-xl border border-amber-100 dark:border-amber-500/10"
              v-if="car.additional_rules"
            >
              <p
                class="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1"
              >
                Additional Rules
              </p>
              <p class="text-sm italic text-amber-800 dark:text-amber-400">
                {{ car.additional_rules }}
              </p>
            </div>
          </div>

          <div>
            <h3
              class="text-sm font-black uppercase tracking-widest text-primary mb-4"
            >
              {{ $t("photo_gallery") }}
            </h3>
            <div
              v-if="car.images?.length"
              class="grid grid-cols-2 md:grid-cols-3 gap-4"
            >
              <div
                v-for="img in car.images"
                :key="img.id"
                class="relative group aspect-video overflow-hidden rounded-xl border dark:border-white/5 shadow-sm"
              >
                <img
                  :src="img.image_path"
                  class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span
                  v-if="img.is_primary"
                  class="absolute top-2 left-2 bg-primary text-white text-[8px] font-black uppercase px-2 py-0.5 rounded shadow-lg z-10"
                >
                  Primary
                </span>
                <div
                  class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <button
                    class="text-white text-xs font-bold uppercase tracking-widest"
                  >
                    View Full
                  </button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center py-10 border-2 border-dashed rounded-2xl dark:border-white/5 text-slate-400"
            >
              {{ $t("no_photos") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column (Pricing & Owner) -->
      <div class="space-y-6">
        <!-- Pricing Card -->
        <div
          class="bg-primary text-white shadow-2xl shadow-primary/30 rounded-3xl p-6 relative overflow-hidden group transition-all duration-500 hover:-translate-y-1"
        >
          <div
            class="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all"
          ></div>
          <h3
            class="text-lg font-black mb-6 flex items-center gap-2 uppercase tracking-tight"
          >
            <span>💰</span> {{ $t("rental_rates") }}
          </h3>
          <div class="space-y-4 relative z-10">
            <div
              class="flex justify-between items-center bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/5"
            >
              <span
                class="text-[10px] font-black opacity-80 uppercase tracking-widest"
                >{{ $t("daily") }}</span
              >
              <span class="text-2xl font-black"
                >{{ car.price_per_day }}
                <span class="text-sm opacity-60 uppercase">{{
                  car.currency
                }}</span></span
              >
            </div>
            <div
              class="flex justify-between items-center bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/5"
            >
              <span
                class="text-[10px] font-black opacity-80 uppercase tracking-widest"
                >{{ $t("weekly") }}</span
              >
              <span class="text-2xl font-black"
                >{{ car.price_per_week }}
                <span class="text-sm opacity-60 uppercase">{{
                  car.currency
                }}</span></span
              >
            </div>
            <div
              class="flex justify-between items-center bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/5"
            >
              <span
                class="text-[10px] font-black opacity-80 uppercase tracking-widest"
                >{{ $t("monthly") }}</span
              >
              <span class="text-2xl font-black"
                >{{ car.price_per_month }}
                <span class="text-sm opacity-60 uppercase">{{
                  car.currency
                }}</span></span
              >
            </div>
            <div class="pt-2 border-t border-white/10 mt-2">
              <div class="flex justify-between items-center opacity-80 px-2">
                <span class="text-[10px] font-bold uppercase tracking-widest">{{
                  $t("extra_mile_fee")
                }}</span>
                <span class="text-sm font-black"
                  >{{ car.extra_mile_fee }}
                  <span class="text-[10px] uppercase">{{
                    car.currency
                  }}</span></span
                >
              </div>
              <div
                class="flex justify-between items-center opacity-80 px-2 mt-1"
              >
                <span class="text-[10px] font-bold uppercase tracking-widest">{{
                  $t("miles_day")
                }}</span>
                <span class="text-sm font-black"
                  >{{ car.miles_per_day }} Mi</span
                >
              </div>
              <div
                class="flex justify-between items-center opacity-80 px-2 mt-1"
              >
                <span class="text-[10px] font-bold uppercase tracking-widest">{{
                  $t("delivery_fee_per_km")
                }}</span>
                <span class="text-sm font-black"
                  >{{ car.delivery_fee_per_km }}
                  <span class="text-[10px] uppercase">{{
                    car.currency
                  }}</span></span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Owner Card -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-3xl p-6 border border-slate-100 dark:border-white/5 transition-colors group"
        >
          <h3
            class="text-sm font-black uppercase tracking-widest text-primary mb-6 flex items-center gap-2 border-b dark:border-white/5 pb-2"
          >
            <span>👤</span> {{ $t("owner_profile") }}
          </h3>
          <div class="space-y-5">
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-slate-100 dark:bg-dark-input rounded-2xl flex items-center justify-center text-xl shadow-inner border dark:border-white/5"
              >
                👤
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >
                  Username
                </p>
                <p class="font-black text-slate-800 dark:text-white truncate">
                  {{ car.user?.username }}
                </p>
              </div>
            </div>

            <div class="space-y-4 pt-2">
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >Email Address</span
                >
                <span
                  class="font-bold text-slate-700 dark:text-slate-300 text-sm break-all"
                  >{{ car.user?.email }}</span
                >
              </div>
              <div v-if="car.user?.phone" class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >Phone Number</span
                >
                <span class="font-bold text-slate-700 dark:text-slate-300">{{
                  car.user.phone
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-3 bg-slate-50 dark:bg-dark-input rounded-xl border dark:border-white/5"
              >
                <span
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >Rating</span
                >
                <div class="flex items-center gap-1">
                  <span class="text-amber-500 font-black text-sm">★</span>
                  <span class="font-black text-sm">{{
                    car.rating_avg || "0.0"
                  }}</span>
                  <span class="text-[10px] text-slate-400"
                    >({{ car.reviews_count }})</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import carService from "@/services/car.service";
import { alertSuccess, alertError, alertConfirm } from "@/utils/alert";

export default {
  components: { BaseButton },
  data() {
    return {
      loading: true,
      car: {
        id: null,
        model_name: "",
        year: "",
        images: [],
        features: [],
        pickup_options: [],
        elevate_experiences: [],
        rules: [],
        brand: {},
        category: {},
        vehicle_type: {},
        fuel_type: {},
        transmission_type: {},
        user: {},
        location: null,
        availability: null,
        protection_plan: null,
        currency: "EGP",
        rating_avg: 0,
        reviews_count: 0,
      },
    };
  },
  mounted() {
    this.fetchCar();
  },
  methods: {
    canManageCar() {
      return this.car.can_edit !== false;
    },

    async fetchCar() {
      this.loading = true;
      try {
        const { id } = this.$route.params;
        const res = await carService.getCar(id, {
          all_cars: this.$route.query.all_cars === "true" || undefined,
        });
        this.car = res.data.data;
      } catch (error) {
        console.error(error);
        alertError("Failed to fetch car details");
      } finally {
        this.loading = false;
      }
    },
    async deleteCar() {
      const confirm = await alertConfirm(
        "Are you sure you want to delete this car?"
      );
      if (confirm.isConfirmed) {
        try {
          await carService.delete(this.car.id);
          alertSuccess("Car deleted successfully");
          this.$router.push("/cars");
        } catch (error) {
          alertError("Failed to delete car");
        }
      }
    },
  },
};
</script>

<style scoped>
.font-arabic {
  font-family: "Tajawal", sans-serif;
  direction: rtl;
}
</style>
